import { Command } from 'cmdk';
import { CornerDownLeft, Search, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Kbd } from '../primitives/Kbd.js';
import '../primitives/dialog.css';
import './palette.css';

export interface PaletteItem {
  id: string;
  label: string;
  /** Muted text after the label (chart #, DOB, CDT description). */
  hint?: string;
  icon?: ReactNode;
  /** Small tag right after the label (e.g. the patient's office code). */
  badge?: ReactNode;
  shortcut?: readonly string[];
  /** Synonyms the matcher should accept (recare, hygiene due, SRP). */
  keywords?: readonly string[];
  onSelect: () => void;
}

export interface PaletteGroup {
  id: string;
  heading: string;
  items: readonly PaletteItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: readonly PaletteGroup[];
  query: string;
  onQueryChange: (query: string) => void;
  /**
   * When true (default) cmdk ranks and filters. Set false when the shell's own
   * index (synonyms, frequency, recency, privacy) supplies already-ranked items.
   */
  filter?: boolean;
  /** "Ask Velora: <query>" as the last row (BUILD-PLAN 3.6). */
  onAsk?: (query: string) => void;
  /** Product short name for the Ask row; pass PRODUCT.shortName. */
  assistantName?: string;
  placeholder?: string;
  loading?: boolean;
}

function itemValue(item: PaletteItem): string {
  return `${item.id} ${item.label}`;
}

const QUESTION_WORDS =
  /^(?:who|what|which|how|when|where|why|show|list|find|my|me|i|is|are|do|does|did|can|could|give|top|total|count|any|all|how's|what's|who's)$/i;

/**
 * Whether palette text reads like a person's name ("Gemma Briarmere"): one to
 * three words of letters, apostrophes, hyphens or periods, not starting with a
 * question word, no digits or question mark. With no matches, such text is a
 * patient the user cannot open, not a question: Enter must not send it to the
 * assistant silently (dentist role test 2026-10-20, finding 24). Pure.
 */
export function looksLikePersonName(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > 60 || /[?\d@#$%&*/\\:;!]/.test(t)) return false;
  const words = t.split(/\s+/);
  if (words.length > 3) return false;
  if (QUESTION_WORDS.test(words[0] ?? '')) return false;
  return words.every((w) => /^\p{L}[\p{L}'’.-]*$/u.test(w));
}

/**
 * The row Enter opens (pure; tested): the best match; with no match, the
 * assistant row only for question-like text. Name-like text (and anything
 * while results are still loading) selects nothing, so Enter does nothing
 * until the user picks "Ask ... instead?" on purpose.
 */
export function paletteDefault(opts: {
  firstItemValue: string | null;
  askValue: string;
  query: string;
  loading: boolean;
}): string {
  if (opts.firstItemValue) return opts.firstItemValue;
  if (!opts.askValue || opts.loading || looksLikePersonName(opts.query)) return '';
  return opts.askValue;
}

/**
 * Enter pressed while the search is still running, with nothing to open yet
 * (assistant role test 2026-10-20: "Tamarackholm" + Enter went to the
 * assistant before the patient arrived). Enter waits; when results arrive,
 * the first one opens. Pure: what to do with a pending Enter now.
 */
export function pendingEnterAction(o: {
  pending: boolean;
  loading: boolean;
  hasResult: boolean;
}): 'wait' | 'open' | 'drop' | 'none' {
  if (!o.pending) return 'none';
  if (o.loading) return 'wait';
  return o.hasResult ? 'open' : 'drop';
}

/**
 * Ctrl/Cmd+K palette (BUILD-PLAN 3.6). Presentational: the shell owns the
 * registry, the shortcut binding and the index; this renders and navigates.
 */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
  query,
  onQueryChange,
  filter = true,
  onAsk,
  assistantName = 'Assistant',
  placeholder = 'Search patients, appointments, codes, or type a command',
  loading = false,
}: CommandPaletteProps) {
  const run = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };
  // With pre-ranked items (filter=false) cmdk keeps whatever row was active
  // before async results arrived (often the Ask row). Select the top result
  // whenever the result list changes, so Enter opens the best match.
  const askValue = onAsk && query.trim() ? `__ask ${query}` : '';
  const firstItem = groups.find((g) => g.items.length > 0)?.items[0];
  const topValue = paletteDefault({
    firstItemValue: firstItem ? itemValue(firstItem) : null,
    askValue,
    query,
    loading,
  });
  const nameLike = !firstItem && !loading && looksLikePersonName(query);
  const resultsKey = `${query}::${groups.map((g) => g.items.map((i) => i.id).join(',')).join('|')}`;
  const [selected, setSelected] = useState(topValue);
  useEffect(() => {
    setSelected(topValue);
    // topValue is derived from resultsKey; reselect only when results change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey]);
  const selection = filter ? {} : { value: selected, onValueChange: setSelected };
  // Enter before the results: open the first one when it arrives (never the assistant).
  const pendingEnter = useRef(false);
  useEffect(() => {
    pendingEnter.current = false;
  }, [query]);
  useEffect(() => {
    const act = pendingEnterAction({
      pending: pendingEnter.current,
      loading,
      hasResult: Boolean(firstItem),
    });
    if (act === 'wait' || act === 'none') return;
    pendingEnter.current = false;
    if (act === 'open' && firstItem) run(firstItem.onSelect);
    // run and firstItem follow the results; reacting to loading and the result list is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, resultsKey]);
  const onKeyDownCapture = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || !loading || firstItem) return;
    e.preventDefault();
    e.stopPropagation();
    pendingEnter.current = true;
  };
  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      shouldFilter={filter}
      {...selection}
      loop
      overlayClassName="vel-overlay vel-palette-overlay"
      contentClassName="vel-palette"
      onKeyDownCapture={onKeyDownCapture}
    >
      <div className="vel-palette__input-row">
        <Search size={16} aria-hidden />
        <Command.Input
          className="vel-palette__input"
          value={query}
          onValueChange={onQueryChange}
          placeholder={placeholder}
        />
        <Kbd keys={['Esc']} />
      </div>
      <Command.List className="vel-palette__list">
        {loading ? <Command.Loading>Searching</Command.Loading> : null}
        <Command.Empty className="vel-palette__empty">
          No results{query ? ` for "${query}"` : ''}
        </Command.Empty>
        {groups.map((g) =>
          g.items.length > 0 ? (
            <Command.Group key={g.id} heading={g.heading} className="vel-palette__group">
              {g.items.map((item) => (
                <Command.Item
                  key={item.id}
                  value={itemValue(item)}
                  keywords={[
                    item.label,
                    ...(item.hint ? [item.hint] : []),
                    ...(item.keywords ?? []),
                  ]}
                  onSelect={() => run(item.onSelect)}
                  className="vel-palette__item"
                >
                  {item.icon ? <span className="vel-palette__icon">{item.icon}</span> : null}
                  <span className="vel-palette__label">{item.label}</span>
                  {item.badge ? <span className="vel-palette__badge">{item.badge}</span> : null}
                  {item.hint ? <span className="vel-palette__hint">{item.hint}</span> : null}
                  {item.shortcut ? (
                    <Kbd
                      keys={item.shortcut}
                      sequence={item.shortcut.length > 1 && item.shortcut[0] === 'G'}
                    />
                  ) : null}
                </Command.Item>
              ))}
            </Command.Group>
          ) : null,
        )}
        {onAsk && query.trim() ? (
          <Command.Group heading={assistantName} className="vel-palette__group" forceMount>
            <Command.Item
              value={askValue || `__ask ${query}`}
              forceMount
              onSelect={() => run(() => onAsk(query))}
              className="vel-palette__item vel-palette__ask"
            >
              <span className="vel-palette__icon vel-palette__ask-icon">
                <Sparkles size={14} aria-hidden />
              </span>
              <span className="vel-palette__label">
                {nameLike ? (
                  <>
                    No patient found for <strong>{query}</strong>. Ask {assistantName} instead?
                  </>
                ) : (
                  <>
                    Ask {assistantName}: <strong>{query}</strong>
                  </>
                )}
              </span>
            </Command.Item>
          </Command.Group>
        ) : null}
      </Command.List>
      <div className="vel-palette__foot" aria-hidden>
        <span>
          <Kbd keys={['Up']} /> <Kbd keys={['Down']} /> to move
        </span>
        <span>
          <CornerDownLeft size={12} /> to open
        </span>
      </div>
    </Command.Dialog>
  );
}
